import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import {
  HpiDataTable,
  HpiDateFilterBar,
  HpiFilterSelect,
  HpiPagination,
  HpiStatsBar,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { parseLocalDate } from "@/lib/date-utils"
import { customerContactPath, customerPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface LogEntry {
  id: number
  log_date: string | null
  customer_id: number
  customer_name: string | null
  customer_user_id: number | null
  contact_name: string | null
  notes: string | null
  employee_name: string | null
  log_type_name: string | null
}

interface Summary {
  total_entries: number
}

interface SelectOption {
  value: number
  label: string
}

interface Props {
  logs: LogEntry[]
  start_date: string
  end_date: string
  log_type_id: string
  employee_id: string
  log_types: SelectOption[]
  employees: SelectOption[]
  summary: Summary
}

/* ── Helpers ────────────────────────────────────────────── */

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "numeric",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

function formatDateRange(start: string, end: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }
  const s = parseLocalDate(start).toLocaleDateString("en-US", opts)
  const e = parseLocalDate(end).toLocaleDateString("en-US", opts)
  return `${s} - ${e}`
}

/* ── Columns ───────────────────────────────────────────── */

const PER_PAGE = 15

const columns: HpiColumn<LogEntry>[] = [
  {
    header: "Contact / Customer",
    width: "max-w-[250px]",
    render: (row) => (
      <div className="text-sm">
        {row.customer_user_id && row.contact_name ? (
          <a
            href={customerContactPath(row.customer_id, row.customer_user_id)}
            className="text-hpi-active-blue hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            {row.contact_name} @ {row.customer_name}
          </a>
        ) : (
          <a
            href={customerPath(row.customer_id)}
            className="text-hpi-active-blue hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            {row.customer_name}
          </a>
        )}
      </div>
    ),
  },
  {
    header: "Notes",
    render: (row) => (
      <div className="text-sm whitespace-pre-wrap">{row.notes}</div>
    ),
  },
  {
    header: "Date",
    width: "max-w-[180px]",
    render: (row) => (
      <div className="text-sm">{formatDateTime(row.log_date)}</div>
    ),
  },
  {
    header: "Employee",
    width: "max-w-[160px]",
    render: (row) => <div className="text-sm">{row.employee_name ?? "-"}</div>,
  },
  {
    header: "Type",
    width: "max-w-[140px]",
    render: (row) => <div className="text-sm">{row.log_type_name ?? "-"}</div>,
  },
]

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Reports", href: "/reports/client_logs" },
  { title: "Client Logs", href: "/reports/client_logs" },
]

export default function ClientLogsReport({
  logs,
  start_date,
  end_date,
  log_type_id,
  employee_id,
  log_types,
  employees,
  summary,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [selectedLogType, setSelectedLogType] = useState(log_type_id)
  const [selectedEmployee, setSelectedEmployee] = useState(employee_id)
  const [page, setPage] = useState(1)

  const totalPages = Math.ceil(logs.length / PER_PAGE)
  const paginated = logs.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const handleUpdate = () => {
    setPage(1)
    router.get(
      "/reports/client_logs",
      {
        start_date: startDate,
        end_date: endDate,
        log_type_id: selectedLogType,
        employee_id: selectedEmployee,
      },
      { preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Client Log Notes" />

      <div className="p-6">
        {/* Date Filter Bar with Type and Employee Selects */}
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        >
          <HpiFilterSelect
            label="Type"
            value={selectedLogType}
            onChange={setSelectedLogType}
            options={log_types}
          />
          <HpiFilterSelect
            label="Employee"
            value={selectedEmployee}
            onChange={setSelectedEmployee}
            options={employees}
          />
        </HpiDateFilterBar>

        {/* Data Table */}
        <HpiDataTable<LogEntry>
          title={`Log Notes  ${formatDateRange(start_date, end_date)}`}
          columns={columns}
          data={paginated}
          keyFn={(row) => row.id}
          emptyMessage="No Log Notes found"
          pagination={
            logs.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={logs.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />

        {/* Summary Stats */}
        <HpiStatsBar
          className="mt-6"
          stats={[
            {
              label: "Total",
              subLabel: "Entries",
              value: summary.total_entries,
            },
          ]}
        />
      </div>
    </AppLayout>
  )
}
